import api from './api';

// Mapowanie danych z API na format frontendu
const mapProductFromAPI = (product) => {
  // Backend zwraca stawka_vat bezpośrednio (ujednolicone nazewnictwo)
  const stawkaVat = product.stawka_vat ?? 23;
  return {
    ...product,
    nazwa: product.name || product.nazwa,
    nazwa_uproszczona: product.nazwa_uproszczona,
    kod_produktu: product.barcode || product.kod_produktu,
    kod_kreskowy: product.barcode || product.ean || product.kod_kreskowy,
    barcode: product.barcode || product.ean || product.kod_kreskowy,
    ean: product.barcode || product.ean || null,
    cena_sprzedazy_brutto: product.price || product.cena_sprzedazy_brutto || 0,
    cena_sprzedazy_netto: product.price_net || product.cena_sprzedazy_netto || ((product.price || 0) / (1 + stawkaVat / 100)),
    cena_zakupu: product.current_purchase_price || product.purchase_price || product.cena_zakupu || 0,
    cena_zakupu_netto: product.purchase_price_netto || product.cena_zakupu_netto || 0,
    cena_zakupu_brutto: product.current_purchase_price || product.cena_zakupu_brutto || product.cena_zakupu || 0,
    stawka_vat: stawkaVat,
    kategoria: product.category || product.kategoria,
    // Mapowanie stanu magazynowego
    stock_quantity: product.stock_quantity ?? product.stan_magazynowy ?? 0,
    stan_magazynowy: product.stock_quantity ?? product.stan_magazynowy ?? 0,
    // Dodane pola jednostek
    gramatura: product.gramatura,
    ilosc_jednostek: product.ilosc_jednostek,
    jednostka_wagi: product.jednostka_wagi
  };
};

// Mapowanie danych z frontendu na format API
const mapProductToAPI = (product) => {
  const mapped = {
    ...product,
    name: product.nazwa || product.name,
    barcode: product.kod_produktu || product.barcode
  };
  return mapped;
};

export const productService = {
  /**
   * Pobierz wszystkie produkty
   * @param {number} limit - Limit wyników (default: 100)
   * @returns {Promise} Lista produktów
   */
  async getProducts(limit = 100) {
    try {
      const response = await api.get('/products', {
        params: {
          limit
        }
      });
      const products = response.data.data || [];
      return products.map(mapProductFromAPI);
    } catch (error) {
      console.error('Błąd pobierania produktów:', error);
      throw error;
    }
  },

  /**
   * Wyszukiwanie produktów
   * @param {Object} options - Opcje wyszukiwania
   * @param {string} options.search - Wyszukiwana fraza
   * @param {string} options.query - Wyszukiwana fraza (alternatywna nazwa)
   * @param {string} options.category - Kategoria produktu
   * @param {number} options.limit - Limit wyników (default: 20)
   * @param {number} options.locationId - ID lokalizacji dla stanów magazynowych
   * @param {number} options.warehouseId - ID magazynu dla stanów magazynowych
   * @returns {Promise} Wyniki wyszukiwania
   */
  async searchProducts(options = {}) {
    try {
      const { search, query, category = '', limit = 20, locationId = null, warehouseId = null } = options;
      const searchQuery = search || query || '';

      const params = { limit };
      if (searchQuery) params.query = searchQuery;
      if (category) params.category = category;
      if (locationId) params.location_id = locationId;
      if (warehouseId) params.warehouse_id = warehouseId;

      const response = await api.get('/products/search', { params });
      const data = response.data;

      // API zwraca: { success, data: { products: [...], total, ... }, message }
      // Normalizuj do { products: [...], total, ... }
      let result = {};
      if (data.data && data.data.products) {
        // Struktura z zagnieżdżonym data
        result.products = data.data.products.map(mapProductFromAPI);
        result.total = data.data.total || data.data.products.length;
      } else if (data.products && Array.isArray(data.products)) {
        // Płaska struktura
        result.products = data.products.map(mapProductFromAPI);
        result.total = data.total || data.products.length;
      } else {
        result.products = [];
        result.total = 0;
      }

      return result;
    } catch (error) {
      console.error('Błąd wyszukiwania produktów:', error);
      throw error;
    }
  },

  /**
   * Pobierz szczegóły produktu
   * @param {number} productId - ID produktu
  /**
   * Pobierz pojedynczy produkt
   * @param {number} productId - ID produktu
   * @param {number} locationId - ID lokalizacji dla stanu magazynowego
   * @returns {Promise} Dane produktu
   */
  async getProduct(productId, locationId = null) {
    try {
      const response = await api.get(`/products/${productId}`, {
        params: { location_id: locationId }
      });
      return {
        success: true,
        data: mapProductFromAPI(response.data.data || response.data)
      };
    } catch (error) {
      console.error('Błąd pobierania produktu:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Pobierz kategorie produktów
   * @returns {Promise} Lista kategorii
   */
  async getCategories() {
    try {
      const response = await api.get('/products/categories');
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania kategorii:', error);
      throw error;
    }
  },

  /**
   * Pobierz statystyki produktów
   * @returns {Promise} Statystyki
   */
  async getProductsStats() {
    try {
      const response = await api.get('/products/stats');
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania statystyk produktów:', error);
      throw error;
    }
  },

  /**
   * Pobierz produkty z niską marżą
   * @param {number} threshold - Próg marży (default: 15)
   * @param {number|string} [locationId] - Lokalizacja/magazyn (koszt FIFO per-lokalizacja)
   * @returns {Promise} Lista produktów z niską marżą
   */
  async getLowMarginProducts(threshold = 15, locationId = null) {
    try {
      const locParam = locationId ? `&location_id=${encodeURIComponent(locationId)}` : '';
      const response = await api.get(`/marze/products?low_margin=${threshold}${locParam}`);
      return {
        success: true,
        data: response.data?.data || []
      };
    } catch (error) {
      console.error('Błąd pobierania produktów z niską marżą:', error);
      return {
        success: false,
        data: []
      };
    }
  },

  /**
   * Pobierz produkt po kodzie kreskowym
   * @param {string} barcode - Kod kreskowy
   * @returns {Promise} Dane produktu
   */
  async getProductByBarcode(barcode) {
    try {
      // Uzyj endpointu EAN ktory sprawdza tez product_ean_codes
      const response = await api.get(`/ean/${barcode}`);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Dodaj nowy produkt
   * @param {Object} productData - Dane produktu
   * @returns {Promise} Wynik operacji
   */
  async addProduct(productData) {
    try {
      const mappedData = mapProductToAPI(productData);
      const response = await api.post('/products', mappedData);
      return response.data;
    } catch (error) {
      console.error('Błąd dodawania produktu:', error);
      throw error;
    }
  },

  /**
   * Aktualizuj produkt
   * @param {number} productId - ID produktu
   * @param {Object} productData - Dane produktu
   * @returns {Promise} Wynik operacji
   */
  async updateProduct(productId, productData) {
    try {
      const mappedData = mapProductToAPI(productData);
      const response = await api.put(`/products/${productId}`, mappedData);
      return response.data;
    } catch (error) {
      console.error('Błąd aktualizacji produktu:', error);
      throw error;
    }
  },

  /**
   * Wyszukiwanie produktów w magazynie (dla POS)
   * @param {Object} options - Opcje wyszukiwania
   * @param {string} options.search - Wyszukiwana fraza
   * @param {string} options.query - Wyszukiwana fraza (alternatywna nazwa)
   * @param {string} options.category - Kategoria produktu
   * @param {number} options.limit - Limit wyników (default: 50)
   * @param {number} options.locationId - ID lokalizacji dla stanów magazynowych
   * @param {boolean} options.availableOnly - Tylko produkty z dodatnim stanem (default: true)
   * @param {AbortSignal} options.signal - AbortController signal dla anulowania requestu
   * @returns {Promise} Wyniki wyszukiwania z magazynu
   */
  async getInventoryProducts(options = {}) {
    try {
      const { search, query, category = '', limit = 50, locationId = null, availableOnly = true, signal = null } = options;
      const searchQuery = search || query || '';

      const params = { limit };
      if (searchQuery) params.search = searchQuery;
      if (category) params.category = category;
      if (locationId) params.location_id = locationId;
      if (availableOnly) params.available_only = 1;

      const requestConfig = { params };
      if (signal) requestConfig.signal = signal;

      const response = await api.get('/products/inventory', requestConfig);
      const data = response.data;

      // API zwraca { success: true, data: { products: [...], pagination: {...} } }
      if (data.success && data.data && data.data.products) {
        const mappedProducts = data.data.products.map(mapProductFromAPI);
        return {
          success: true,
          products: mappedProducts,
          pagination: data.data.pagination
        };
      } else {
        return {
          success: false,
          products: [],
          message: data.message || 'Brak danych'
        };
      }
    } catch (error) {
      // Re-throw AbortError bez logowania
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        throw error;
      }
      console.error('Błąd wyszukiwania produktów w magazynie:', error);
      throw error;
    }
  },

  /**
   * Usuń produkt
   * @param {number} productId - ID produktu
   * @returns {Promise} Wynik operacji
   */
  async deleteProduct(productId) {
    try {
      const response = await api.delete(`/products/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Błąd usuwania produktu:', error);
      throw error;
    }
  },

  /**
   * Ustaw cenę specjalną produktu dla magazynu
   * @param {number} productId - ID produktu
   * @param {number} warehouseId - ID magazynu
   * @param {number} cenaBrutto - Cena brutto
   * @param {number} taxRate - Stawka VAT (default: 23)
   * @returns {Promise} Wynik operacji
   */
  async setSpecialPrice(productId, warehouseId, cenaBrutto, taxRate = 23) {
    try {
      const cenaNetto = cenaBrutto / (1 + taxRate / 100);
      const response = await api.put(`/warehouses/${warehouseId}/products/${productId}/price`, {
        cena_sprzedazy_brutto: cenaBrutto,
        cena_sprzedazy_netto: Math.round(cenaNetto * 100) / 100
      });
      return response.data;
    } catch (error) {
      console.error('Błąd ustawiania ceny specjalnej:', error);
      throw error;
    }
  },

  /**
   * Usuń cenę specjalną produktu
   * Backend endpoint: DELETE /api/warehouses/<warehouse_id>/products/<product_id>/price
   * Wymaga data_od w body (data od kiedy cena obowiązuje)
   * @param {number} productId - ID produktu
   * @param {number} warehouseId - ID magazynu
   * @returns {Promise} Wynik operacji
   */
  async removeSpecialPrice(productId, warehouseId) {
    try {
      // Pobierz aktualną cenę specjalną aby uzyskać data_od
      const priceResponse = await api.get(`/warehouses/${warehouseId}/products/${productId}/price`);
      const priceData = priceResponse.data?.data;
      if (priceData && priceData.data_od) {
        const response = await api.delete(`/warehouses/${warehouseId}/products/${productId}/price`, {
          data: { data_od: priceData.data_od }
        });
        return response.data;
      }
      return { success: true };
    } catch (error) {
      console.error('Błąd usuwania ceny specjalnej:', error);
      throw error;
    }
  }
};
